// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { styledBoxStyle } from "@keller/App/Components/Common/StyledBox";
import xOrderInformationStyles from "@keller/App/SharedStyle/MyAccount/Details/OrderInformationStyles";
import { xIsPickup } from "@keller/App/Store/Selectors/GeneralSelectors";
import { ShippingAddressInfo } from "@keller/XDL/types";
import { color, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { useContext } from "react";
import { css } from "styled-components";

export interface orderDetailsPickUpShippingAddressInfoStyles {
    titleText?: TypographyProps;
    addressDisplay?: AddressInfoDisplayStyles;
    wrapper?: InjectableCss;
}

export const orderDetailsPickUpShippingAddressInfoStyles: orderDetailsPickUpShippingAddressInfoStyles = {
    wrapper: {
        css: css`
            padding-bottom: 10px;

            ${xScreen("lte", "sm")} {
                padding-bottom: 0;
            }

            @media print {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                padding-bottom: 0;
            }
        `,
    },
    titleText: {
        ...mergeWithCss(xOrderInformationStyles.address?.heading, {
            css: css`
                margin-top: 10px;
                @media print {
                    background-color: ${color("common.white")};
                    border: 1px solid ${color("common.border")};
                    border-bottom: 0;
                    font-size: 12px;
                    line-height: 18px;
                    padding: 8px;
                    margin: 0;
                }
            `,
        }),
    },

    addressDisplay: {
        wrapper: {
            css: css`
                flex-direction: column;
                padding-top: 0;
                border-radius: 4px;
                ${styledBoxStyle}

                ${xScreen("lte", "sm")} {
                    padding-top: 15px;
                }

                @media print {
                    border-radius: 0;
                    padding: 8px;
                    flex-grow: 1;
                }
            `,
        },
    },
};

const styles = orderDetailsPickUpShippingAddressInfoStyles;

const OrderDetailsPickUpShippingAddressInfo: React.FunctionComponent = () => {
    const { value: order } = useContext(OrderStateContext);
    if (!order) {
        return null;
    }

    const isPickUpFulfillmentMethod = xIsPickup(order.fulfillmentMethod);

    if (!isPickUpFulfillmentMethod || !order.properties.shippingAddressInfo) {
        return null;
    }

    const shippingAddressInfo = JSON.parse(order.properties.shippingAddressInfo) as ShippingAddressInfo;
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="orderDetailsPickUpShippingAddressInfo">
            <Typography {...styles.titleText}>{translate("Shipping Address")}</Typography>
            <AddressInfoDisplay {...shippingAddressInfo} extendedStyles={styles.addressDisplay} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsPickUpShippingAddressInfo,
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
